import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dateCell", "modal", "hourDial", "minuteDial", "selectedDatesList", "datesInput", "hourInput", "minuteInput", "copyStatus", "form", "sessionsList", "noSessionsMessage", "clearButton"]

  connect() {
    this.selectedDates = []
    this.isCopyMode = false
    this.sourceSession = null
  }

  toggleDate(event) {
    const date = event.currentTarget.dataset.date
    const cell = event.currentTarget

    if (this.selectedDates.includes(date)) {
      this.selectedDates = this.selectedDates.filter(d => d !== date)
      cell.classList.remove("bg-blue-600", "text-white")
      cell.classList.add("bg-white")
    } else {
      this.selectedDates.push(date)
      cell.classList.remove("bg-white")
      cell.classList.add("bg-blue-600", "text-white")
    }

    this.updateSelectedDatesUI()
    this.filterSessions()
  }

  filterSessions() {
    if (!this.hasSessionsListTarget) return

    const sessions = this.sessionsListTarget.querySelectorAll('.session-card')
    
    if (this.selectedDates.length === 0) {
      // If no dates selected, show all sessions
      sessions.forEach(session => session.classList.remove('hidden'))
      if (this.hasNoSessionsMessageTarget) this.noSessionsMessageTarget.classList.add('hidden')
      return
    }

    let visibleCount = 0
    sessions.forEach(session => {
      const sessionDate = session.dataset.date
      if (this.selectedDates.includes(sessionDate)) {
        session.classList.remove('hidden')
        visibleCount++
      } else {
        session.classList.add('hidden')
      }
    })

    if (this.hasNoSessionsMessageTarget) {
      if (visibleCount === 0) {
        this.noSessionsMessageTarget.classList.remove('hidden')
      } else {
        this.noSessionsMessageTarget.classList.add('hidden')
      }
    }
  }

  clearDates() {
    this.selectedDates = []
    this.dateCellTargets.forEach(cell => {
      cell.classList.remove("bg-blue-600", "text-white")
      cell.classList.add("bg-white")
    })
    this.updateSelectedDatesUI()
    this.filterSessions()
  }

  updateSelectedDatesUI() {
    if (this.hasSelectedDatesListTarget) {
      this.selectedDatesListTarget.textContent = this.selectedDates.length > 0 
        ? `${this.selectedDates.length} dates selected`
        : "No dates selected"
    }

    if (this.hasClearButtonTarget) {
      if (this.selectedDates.length > 0) {
        this.clearButtonTarget.classList.remove('hidden')
      } else {
        this.clearButtonTarget.classList.add('hidden')
      }
    }
    
    if (this.hasDatesInputTarget) {
      // Clear existing hidden inputs for dates
      const container = this.datesInputTarget.parentElement
      container.querySelectorAll('input[name="dates[]"]').forEach(el => el.remove())
      
      // Add new ones
      this.selectedDates.forEach(date => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'dates[]'
        input.value = date
        container.appendChild(input)
      })
    }
  }

  disconnect() {
    this.toggleScrollLock(false, true)
  }

  openTimePicker() {
    if (this.selectedDates.length === 0) {
      alert("Please select at least one date first.")
      return
    }

    // Highlight the dials based on hidden input values
    const hour = this.hourInputTarget.value
    const minute = parseInt(this.minuteInputTarget.value)

    this.hourDialTarget.querySelectorAll('button').forEach(btn => {
      if (parseInt(btn.dataset.hour) === parseInt(hour)) {
        btn.classList.remove("bg-gray-50", "text-gray-900")
        btn.classList.add("bg-blue-600", "text-white")
      } else {
        btn.classList.remove("bg-blue-600", "text-white")
        btn.classList.add("bg-gray-50", "text-gray-900")
      }
    })

    this.minuteDialTarget.querySelectorAll('button').forEach(btn => {
      if (parseInt(btn.dataset.minute) === minute) {
        btn.classList.remove("bg-gray-50", "text-gray-900")
        btn.classList.add("bg-blue-600", "text-white")
      } else {
        btn.classList.remove("bg-blue-600", "text-white")
        btn.classList.add("bg-gray-50", "text-gray-900")
      }
    })

    this.modalTarget.classList.remove("hidden")
    this.modalTarget.classList.add("flex")
    this.toggleScrollLock(true)
  }

  closeModal() {
    this.modalTarget.classList.add("hidden")
    this.modalTarget.classList.remove("flex")
    this.toggleScrollLock(false)
    this.isCopyMode = false
    this.sourceSession = null
  }

  toggleScrollLock(lock, force = false) {
    const body = document.body
    const html = document.documentElement
    let openModals = parseInt(body.dataset.openModals || "0")

    if (lock) {
      openModals++
      body.dataset.openModals = openModals
      
      if (openModals === 1) {
        this.scrollPos = window.pageYOffset
        body.style.position = 'fixed'
        body.style.top = `-${this.scrollPos}px`
        body.style.width = '100%'
        body.classList.add('overflow-hidden')
        html.classList.add('overflow-hidden')
      }
    } else {
      openModals = force ? 0 : Math.max(0, openModals - 1)
      body.dataset.openModals = openModals
      
      if (openModals === 0) {
        const scrollY = body.style.top
        body.style.position = ''
        body.style.top = ''
        body.style.width = ''
        body.classList.remove('overflow-hidden')
        html.classList.remove('overflow-hidden')
        if (!force) {
          window.scrollTo(0, parseInt(scrollY || '0') * -1)
        }
      }
    }
  }

  selectHour(event) {
    const hour = event.currentTarget.dataset.hour
    this.hourInputTarget.value = hour
    
    // UI feedback for dials
    this.hourDialTarget.querySelectorAll('button').forEach(btn => {
      btn.classList.remove("bg-blue-600", "text-white")
      btn.classList.add("bg-gray-50", "text-gray-900")
    })
    event.currentTarget.classList.remove("bg-gray-50", "text-gray-900")
    event.currentTarget.classList.add("bg-blue-600", "text-white")
  }

  selectMinute(event) {
    const minute = event.currentTarget.dataset.minute
    this.minuteInputTarget.value = minute
    
    // UI feedback for dials
    this.minuteDialTarget.querySelectorAll('button').forEach(btn => {
      btn.classList.remove("bg-blue-600", "text-white")
      btn.classList.add("bg-gray-50", "text-gray-900")
    })
    event.currentTarget.classList.remove("bg-gray-50", "text-gray-900")
    event.currentTarget.classList.add("bg-blue-600", "text-white")
  }

  startCopy(event) {
    const sessionId = event.currentTarget.dataset.sessionId
    const timeStr = event.currentTarget.dataset.timeStr
    const [hour, minute] = timeStr.split(':')

    this.isCopyMode = true
    this.sourceSession = { hour, minute }
    
    // Auto-set the dials
    this.hourInputTarget.value = hour
    this.minuteInputTarget.value = minute

    // Show copy status
    this.copyStatusTarget.classList.remove("hidden")
    this.copyStatusTarget.querySelector(".time-display").textContent = timeStr
    
    // Scroll to the dials in the modal if it was open, but usually we click copy from the list
    // so we just show the message and tell them to select dates
    alert(`Copying ${timeStr}. Now select the dates on the calendar you want to add this time to.`)
  }

  cancelCopy() {
    this.isCopyMode = false
    this.sourceSession = null
    this.copyStatusTarget.classList.add("hidden")
  }

  submitBulk(event) {
    if (this.selectedDates.length === 0) {
      event.preventDefault()
      alert("Please select dates first.")
      return
    }
    // Form will submit with the hidden inputs
  }
}
